import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { REIButton, REICheckBox, REIProgressSpinner } from "../ui";
import { ApiEndPoints } from "../../utils/endpoints";
import {
  IS_COUPONCODE_APPLIED,
  IS_MONTHLY_CASUAL_PLAN,
  MEMBER_KEY,
  ROLE_MODULES,
  TERMS_CONDITIONS_URL,
  USER_DETAIL,
} from "../../utils/constants";
import { getUserRoles, setUserRoles } from "../../utils/userRoles";
import { formatCurrencyWithDecimal } from "../../utils/commonUtil";
import { httpPost, useHttpGets } from "../../services/httpService";

export interface IUpgradeQuote {
  currentPlanName: string;
  targetPlanName: string;
  newPlanPrice: number;
  proRataCredit: number;
  balancePayable: number;
  nextBillingDate?: string;
}

export interface IUpgradeResult {
  success: boolean;
  membershipTypes?: string[];
  message?: string;
}

interface IUpgradeToProfessionalProps {
  onUpgraded?: (result: IUpgradeResult) => void;
}

type Step = "terms" | "payment" | "processing" | "success";

const TEAL_BUTTON =
  "rounded-lg bg-brand-teal hover:bg-brand-tealDark border-none px-8 py-3.5 text-sm md:text-base font-semibold text-white shadow-md transition-all w-full sm:w-auto";

const UpgradeToProfessional: React.FC<IUpgradeToProfessionalProps> = ({ onUpgraded }) => {
  const toast = useRef<Toast>(null);
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState<Step>("terms");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [balanceAccepted, setBalanceAccepted] = useState(false);
  const [quote, setQuote] = useState<IUpgradeQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [upgradeResult, setUpgradeResult] = useState<IUpgradeResult | null>(null);
  const newPlanPrice = useRef<number>(0);
  const proRataCredit = useRef<number>(0);
  const memberId = localStorage.getItem(MEMBER_KEY) ?? "";
  const memberDetail = useMemo(() => JSON.parse(localStorage.getItem(USER_DETAIL) || "{}"), []);
  const buttonLabel = "Upgrade to Professional";
  const idPrefix = "upgpro";
  const billingCycle = "monthly";
  const isCouponApplied = localStorage.getItem(IS_COUPONCODE_APPLIED) === "true";

  const resetDialog = () => {
    setStep("terms");
    setTermsAccepted(false);
    setBalanceAccepted(false);
    setQuote(null);
    setUpgradeResult(null);
  };

  const closeDialog = () => {
    const wasSuccessful = step === "success";
    setVisible(false);
    resetDialog();
    if (wasSuccessful) {
      onUpgraded?.(upgradeResult ?? { success: true });
      window.location.reload();
    }
  };

  useEffect(() => {
    const memberId = localStorage.getItem(MEMBER_KEY);
    if (!memberId) return;
    newPlanPrice.current = isCouponApplied ? 129 : 149;
    proRataCredit.current = isCouponApplied ? 79 : 99;
  }, []);

  const loadQuote = () => {
    setQuote({
      currentPlanName: "Starter",
      targetPlanName: "Professional",
      newPlanPrice: newPlanPrice.current,
      proRataCredit: proRataCredit.current,
      balancePayable:
        newPlanPrice.current - proRataCredit.current > 0 ? newPlanPrice.current - proRataCredit.current : 0,
    });
  };

  const handleTermsChange = (checked: boolean) => {
    setTermsAccepted(checked);
    if (!checked) {
      setBalanceAccepted(false);
      setQuote(null);
      setStep("terms");
      return;
    }
    if (!quote) {
      loadQuote();
      setStep("payment");
    }
  };

  const handleConfirm = async () => {
    if (!termsAccepted || !balanceAccepted) return;

    setStep("processing");
    const getErrorMessage = (error: unknown) => {
      if (typeof error === "string") return error;
      if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
        return error.message;
      }
      return "We could not complete your upgrade. Please try again.";
    };

    try {
      if (!memberId.trim()) {
        throw new Error("Please sign in again before upgrading.");
      }

      const paymentMethods = await useHttpGets(`${ApiEndPoints.myaccount}/GetPaymentMethods/`, { memberId });
      const cards = paymentMethods.data?.creditPaymentMethods;
      if (
        paymentMethods.status !== 200 ||
        paymentMethods.error ||
        paymentMethods.data?.success === false ||
        !Array.isArray(cards)
      ) {
        throw new Error("Unable to verify payment methods. Please try again.");
      }

      const now = new Date();
      const hasUnexpiredCard = cards.some((card) => {
        if (card?.isDefault !== true) return false;

        const year = card?.creditCardExpirationYear;
        const month = card?.creditCardExpirationMonth;
        if (!Number.isInteger(year) || year < 1 || !Number.isInteger(month) || month < 1 || month > 12) {
          return false;
        }

        return year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 2);
      });

      if (!hasUnexpiredCard) {
        setStep("payment");
        toast.current?.show({
          severity: "warn",
          summary: "Valid default card required",
          detail: "Set an unexpired card as your default in Payment Methods before upgrading.",
          life: 5000,
        });
        return;
      }

      const result = await httpPost(`${ApiEndPoints.myaccount}/${memberId}/upgrade`, null);

      if (result.status === 200) {
        setUserRoles([
          ...getUserRoles().filter((role) => role?.Module !== ROLE_MODULES.STARTER),
          { Module: ROLE_MODULES.RP_DATA },
        ]);
        localStorage.setItem(IS_MONTHLY_CASUAL_PLAN, "false");
        setUpgradeResult({ success: true, membershipTypes: (result.data as any)?.membershipTypes });
        setStep("success");
      } else {
        setStep("payment");
        setVisible(false);
        toast.current?.show({
          severity: "error",
          summary: "Upgrade failed",
          detail: getErrorMessage(result.error),
          life: 5000,
        });
      }
    } catch (error: any) {
      setStep("payment");
      setVisible(false);
      toast.current?.show({
        severity: "error",
        summary: "Upgrade failed",
        detail: getErrorMessage(error),
        life: 5000,
      });
    }
  };

  return (
    <>
      <Toast ref={toast} position="top-right" appendTo={document.body} baseZIndex={12000} />
      <REIButton
        id={`${idPrefix}-btn-open`}
        label={buttonLabel}
        icon="pi pi-arrow-right"
        iconPos="right"
        onClick={() => setVisible(true)}
        classNames={TEAL_BUTTON}
      />

      <Dialog
        id={`${idPrefix}-dlg-upgrade`}
        header="Upgrade to Professional Membership"
        visible={visible}
        style={{ width: "90vw", maxWidth: "560px" }}
        onHide={closeDialog}
        draggable={false}
        closable={step !== "processing"}
        className="rei-dialog"
      >
        {step === "processing" && (
          <div className="flex flex-col items-center gap-4 py-8">
            <REIProgressSpinner />
            <p className="text-slate-700 font-semibold">Processing your upgrade...</p>
          </div>
        )}

        {step === "success" && (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <div className="w-16 h-16 rounded-full bg-brand-teal/10 flex items-center justify-center">
              <i className="pi pi-check-circle text-4xl text-brand-teal" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 m-0">Upgrade successful</h3>
            <p className="text-slate-600 m-0">
              Your Professional membership is now active and RP Data access has been enabled. A welcome email is on its
              way to {memberDetail.EmailAddress}.
            </p>
            <REIButton id={`${idPrefix}-btn-done`} label="Done" onClick={closeDialog} classNames={TEAL_BUTTON} />
          </div>
        )}

        {(step === "terms" || step === "payment") && (
          <div className="flex flex-col gap-4 pt-2">
            <p className="text-xs text-slate-600 m-0">
              Ongoing membership fees will then be billed in line with your new billing cycle date until you decide to
              cancel. Cancellation must be done by giving 14 days notice prior to your billing day by email to
              info@realestateinvestar.com.au
            </p>
            <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 border-solid rounded-lg p-3">
              <REICheckBox
                id={`${idPrefix}-chk-terms`}
                checked={termsAccepted}
                disabled={quoteLoading}
                onChange={(e) => handleTermsChange(!!e.checked)}
              />
              <label htmlFor={`${idPrefix}-chk-terms`} className="text-sm text-slate-700 cursor-pointer">
                I have read and accept the{" "}
                <a
                  id={`${idPrefix}-lnk-terms`}
                  data-testid={`${idPrefix}-lnk-terms`}
                  href={TERMS_CONDITIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-teal underline font-semibold"
                >
                  Terms &amp; Conditions
                </a>
                .
              </label>
            </div>

            {quoteLoading && (
              <div className="flex justify-center py-2">
                <REIProgressSpinner />
              </div>
            )}

            {step === "payment" && quote && (
              <div className="rounded-lg border border-slate-200 border-solid p-4 flex flex-col gap-2">
                <div className="flex justify-between text-sm text-slate-700">
                  <span>Professional membership ({billingCycle})</span>
                  <span>{formatCurrencyWithDecimal(quote.newPlanPrice)}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-700">
                  <span>Credit for your current {quote.currentPlanName || "Starter"} plan</span>
                  <span>-{formatCurrencyWithDecimal(quote.proRataCredit)}</span>
                </div>
                <div className="flex justify-between font-bold text-slate-900 border-t border-x-0 border-b-0 border-solid border-slate-200 pt-2">
                  <span>Balance payable today</span>
                  <span>{formatCurrencyWithDecimal(quote.balancePayable)}</span>
                </div>
                {quote.nextBillingDate && (
                  <span className="text-xs text-slate-500">Next billing date: {quote.nextBillingDate}</span>
                )}

                <div className="flex items-start gap-2 mt-2">
                  <REICheckBox
                    id={`${idPrefix}-chk-balance`}
                    checked={balanceAccepted}
                    onChange={(e) => setBalanceAccepted(!!e.checked)}
                  />
                  <label htmlFor={`${idPrefix}-chk-balance`} className="text-sm text-slate-700 cursor-pointer">
                    I accept the balance payment of {formatCurrencyWithDecimal(quote.balancePayable)} to be charged to
                    my registered payment method.
                  </label>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-2">
              <REIButton
                id={`${idPrefix}-btn-cancel`}
                label="Cancel"
                onClick={closeDialog}
                classNames="bg-white text-slate-600 border border-slate-300"
              />
              <REIButton
                id={`${idPrefix}-btn-confirm`}
                label="Confirm & Pay"
                onClick={handleConfirm}
                disabled={!termsAccepted || !balanceAccepted || step !== "payment"}
                classNames={TEAL_BUTTON}
              />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default UpgradeToProfessional;
